import { AxiosRequestConfig } from 'axios';

import api from 'services/api';

import type { BaseTerritoryData } from 'services/basesServices';
import { getMockCustomerStatusSummary } from 'utils/blockingDevMock';

import { getAccessToken } from './accessTokenServices';

export type FiscalStatus = 'ATIVA' | 'SUSPENSA' | 'INAPTA' | 'BAIXADA' | 'NULA';

export type StateRegistration = {
  serasa?: string[];
  salesforce?: string;
  match?: boolean;
};

export type CustomerStatusSummary = {
  fiscal?: {
    status: FiscalStatus;
    regular: boolean;
  };
  registration?: {
    stateRegistration?: StateRegistration;
    /** Classe contribuinte IN68, como vem do Salesforce. */
    taxpayerClass?: { salesforce?: string };
  };
  paymentTerm?: string;
  financial?: {
    hasOverdueBankSlips: boolean;
    overdueCount?: number;
  };
  /**
   * Bases do cliente com o **território embutido** — inclui as flags de
   * bloqueio (`active`, `gasReleased`, `oracleStatus`) dentro de
   * `territory.base`.
   *
   * Dispensa o N+1 de `…/bases/{code}/territory`: o resumo já devolve, numa
   * chamada só, o que antes era buscado base a base.
   */
  bases?: CustomerStatusBase[];
};

export type CustomerStatusBase = {
  addressCode: string;
  /** Código da base instalada (o mesmo `name` de `/customers/me/bases`). */
  name: string;
  nickname: string;
  /** `A` = ativa no Oracle. */
  status?: string;
  territory?: BaseTerritoryData;
  /**
   * Resumo pronto do backend sobre a base estar regular.
   *
   * Regra informada pelo backend (parecer de 2026-10-27):
   * `false` quando `active = false` **ou** `gasReleased = false` **ou**
   * `oracleStatus <> 'Ativa'`.
   *
   * ⚠️ O front **não decide por este campo**: continua avaliando as três
   * flags de `territory.base`, que são as que a estória define (RN06). O
   * motivo é que a segunda metade da regra, como foi escrita, usa "ou" onde
   * deveria usar "e" — por ela, uma base com `active = false` e
   * `gasReleased = true` seria `true` e `false` ao mesmo tempo. Enquanto isso
   * não for esclarecido (P-26), avaliar as flags é o caminho seguro: na
   * dúvida, bloqueia.
   */
  resumeStatus?: boolean;
};

export type CustomerStatusFailure =
  | 'unauthorized'
  | 'not-found'
  | 'bad-request'
  | 'network';

export type CustomerStatusResult =
  | { ok: true; data: CustomerStatusSummary }
  | { ok: false; reason: CustomerStatusFailure };

const STATUS_SUMMARY_URL = 'industrial/customer/v1/customers/status-summary';

type ErrorWithResponse = { response?: { status?: number } };

const failureFromStatus = (status?: number): CustomerStatusFailure => {
  if (status === 401) return 'unauthorized';
  if (status === 404) return 'not-found';
  if (status === 400) return 'bad-request';
  return 'network';
};

export const getCustomerStatusSummary = async (
  siteCode: string
): Promise<CustomerStatusResult> => {
  if (!siteCode) {
    return { ok: false, reason: 'bad-request' };
  }

  const mocked = getMockCustomerStatusSummary();
  if (mocked !== undefined) {
    return mocked === null
      ? { ok: false, reason: 'network' }
      : { ok: true, data: mocked };
  }

  const access_token = await getAccessToken();

  const parameters = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      access_token,
    },
    params: { 'site-code': siteCode },
  } as AxiosRequestConfig;

  try {
    const response = await api.get<CustomerStatusSummary>(
      STATUS_SUMMARY_URL,
      parameters
    );

    return { ok: true, data: response.data ?? {} };
  } catch (error) {
    const status = (error as ErrorWithResponse)?.response?.status;
    return { ok: false, reason: failureFromStatus(status) };
  }
};

export default getCustomerStatusSummary;
